import {
  Condition,
  ConditionOperator,
  ConditionOption,
  DataTableEntityType,
  DataView,
  DataViewType,
  EmptyDataView,
  getDefaultColumnWidth,
  NONVALUE_OPERATORS,
  ViewColumn,
  ViewSort,
} from "@/schema"
import { DataTableColumn } from "@ui/data-table/columns"
import { create } from "zustand"

function filterIsComplete(filter: Condition): boolean {
  if (filter.operator && NONVALUE_OPERATORS.includes(filter.operator as ConditionOperator)) {
    return true
  }
  return !!((filter.value && filter.value.length > 0) || (filter.values && filter.values.length > 0))
}

interface DataQueryState {
  refetch: () => void
  isError: boolean
  isLoading: boolean
  error: Error | null
  fetchNextPage?: () => void
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
}
interface DataTableState {
  // ATTRIBUTES
  isUpdated: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[]
  totalCount?: number
  totalAum?: number
  dataQuery: DataQueryState
  dataView: DataView
  dataViews: DataView[]
  searchTerm: string
  viewType: DataViewType
  columnOptions: DataTableColumn[]
  visibleColumns: DataTableColumn[]
  filterOptions: ConditionOption[]
  originalDataView: DataView
  viewFilters: Condition[]
  completedConditions: Condition[]
  entityType: DataTableEntityType | undefined
  bookIdOverride?: string
  groupKeyMappings?: Record<string, string>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isRowDisabled: (row: any) => boolean
  viewSort?: { id?: string; field?: string; direction?: "asc" | "desc" }

  // SETTERS
  setIsUpdated: (isUpdated: boolean) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setData: (data: any) => void
  setDataView: (dataView: DataView) => void
  setDataViews: (dataViews: DataView[]) => void
  setColumnOptions: (columnOptions: DataTableColumn[]) => void
  setVisibleColumns: () => void
  setSearchTerm: (searchTerm: string) => void
  setFilterOptions: (filterOptions: ConditionOption[]) => void
  setDataQuery: (dataQuery: DataQueryState) => void
  setOriginalDataView: (originalDataView: DataView) => void
  setConditions: (viewFilters: Condition[]) => void
  setViewSort: (viewSort?: { id?: string; field?: string; direction?: "asc" | "desc" }) => void
  setTotalCount: (totalCount: number) => void
  setTotalAum: (totalAum: number) => void
  setGroupKeyMappings: (groupKeyMappings: Record<string, string>) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setIsRowDisabled: (isRowDisabled: (row: any) => boolean) => void
  setBookIdOverride: (bookIdOverride?: string) => void

  // CELL SAVING STATE
  savingCells: Record<string, number> // cellId -> timestamp when saving started
  setSavingCell: (cellId: string) => void
  clearSavingCell: (cellId: string, minDuration?: number) => void

  // METHODS
  reset: () => void
  onSearch: (searchTerm: string) => void
  addViewColumn: (field: string) => void
  removeViewColumn: (field: string) => void
  swapColumns: (columnA: ViewColumn, columnB: ViewColumn) => void
  updateColumnWidth: (field: string, width: number) => void
  resetDataView: () => void
  checkIfDataViewRemainsChanged: () => void
  prepareDataViewForSave: () => Partial<DataView> & { id: string }
  setEntityType: (entityType: DataTableEntityType) => void
}

export const useDataTableStore = create<DataTableState>((set, get) => ({
  isUpdated: false,
  totalCount: undefined,
  totalAum: undefined,
  data: [],
  dataView: EmptyDataView,
  dataViews: [],
  columnOptions: [],
  visibleColumns: [],
  filterOptions: [],
  searchTerm: "",
  viewType: "default",
  originalDataView: EmptyDataView,
  viewFilters: [],
  completedConditions: [],
  viewSort: undefined,
  dataQuery: {
    refetch: () => {},
    fetchNextPage: () => {},
    hasNextPage: false,
    isFetchingNextPage: false,
    isError: false,
    error: null,
    isLoading: true,
  },
  entityType: undefined,
  bookIdOverride: undefined,
  isRowDisabled: () => false,
  savingCells: {},

  setIsUpdated: (isUpdated: boolean) => {
    set({ isUpdated })
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setData: (data: any) => {
    set({ data })
  },
  setVisibleColumns: () => {
    const { dataView, columnOptions, entityType } = get()
    if (!columnOptions?.length) return

    const viewCols = dataView?.view_columns || []
    const hasAnyViewCols = viewCols.length > 0
    const byField = new Map(viewCols.map((vc) => [vc.field, vc]))

    const lockedVisibilityFields = new Set(
      columnOptions.filter((c) => c.lockVisibility).map((c) => c.accessorKey as string)
    )

    const selected = (
      hasAnyViewCols
        ? columnOptions.filter((c) => {
            const vc = byField.get(c.accessorKey as string)
            if (lockedVisibilityFields.has(c.accessorKey as string)) return true
            return vc?.is_visible
          })
        : columnOptions
    )
      .map((c) => {
        const vc = byField.get(c.accessorKey as string)
        const width =
          (vc?.column_width as number | undefined) ??
          (c.size as number | undefined) ??
          getDefaultColumnWidth(entityType, c.accessorKey as string)
        const anchorSortOrder = c.fallbackAfterField ? byField.get(c.fallbackAfterField)?.sort_order : undefined
        const fallbackSortOrder = anchorSortOrder !== undefined ? anchorSortOrder + 0.5 : c.fallbackSortOrder
        return {
          ...c,
          size: width,
          sortOrder: vc?.sort_order ?? fallbackSortOrder,
        }
      })
      .sort((a, b) => {
        if (hasAnyViewCols) return (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
        return 0
      })

    set({ visibleColumns: selected })
  },

  setDataView: (dataView: DataView) => {
    const { setVisibleColumns } = get()
    set({ dataView })
    set({ viewType: dataView.view_type })
    set({ viewFilters: dataView.condition_group.conditions })
    set({ completedConditions: dataView.condition_group.conditions.filter(filterIsComplete) })
    const firstSort = dataView.view_sorts?.[0]
    set({
      viewSort:
        firstSort?.field && firstSort?.direction
          ? { id: firstSort.id, field: firstSort.field, direction: firstSort.direction }
          : undefined,
    })
    setVisibleColumns()
  },

  setEntityType: (entityType: DataTableEntityType) => {
    set({ entityType })
    const { setVisibleColumns } = get()
    setVisibleColumns()
  },

  setBookIdOverride: (bookIdOverride?: string) => {
    set({ bookIdOverride })
  },

  setOriginalDataView: (originalDataView: DataView) => {
    const { setVisibleColumns } = get()
    set({ originalDataView })
    setVisibleColumns()
  },

  setDataViews: (dataViews: DataView[]) => {
    set({ dataViews })
  },

  setColumnOptions: (columnOptions: DataTableColumn[]) => {
    set({ columnOptions })
    const { setVisibleColumns } = get()
    setVisibleColumns()
  },

  setSearchTerm: (searchTerm: string) => {
    set({ searchTerm })
  },

  setFilterOptions: (filterOptions: ConditionOption[]) => {
    set({ filterOptions })
  },

  setDataQuery: (dataQuery: DataQueryState) => {
    set({ dataQuery })
  },

  setGroupKeyMappings: (groupKeyMappings: Record<string, string>) => {
    set({ groupKeyMappings })
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setIsRowDisabled: (isRowDisabled: (row: any) => boolean) => {
    set({ isRowDisabled })
  },

  setSavingCell: (cellId: string) => {
    set((state) => ({
      savingCells: { ...state.savingCells, [cellId]: Date.now() },
    }))
  },

  clearSavingCell: (cellId: string, minDuration = 300) => {
    const { savingCells } = get()
    const startTime = savingCells[cellId]
    if (!startTime) return

    const elapsed = Date.now() - startTime
    const remaining = Math.max(0, minDuration - elapsed)

    setTimeout(() => {
      set((state) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [cellId]: _, ...rest } = state.savingCells
        return { savingCells: rest }
      })
    }, remaining)
  },

  setViewSort: (viewSort?: { id?: string; field?: string; direction?: "asc" | "desc" }) => {
    const { checkIfDataViewRemainsChanged, viewSort: currentSort } = get()
    if (JSON.stringify(currentSort) === JSON.stringify(viewSort)) {
      return
    }
    set({ viewSort })
    set({ isUpdated: true })
    checkIfDataViewRemainsChanged()
  },

  setTotalCount: (totalCount: number) => {
    set({ totalCount })
  },

  setTotalAum: (totalAum: number) => {
    set({ totalAum })
  },

  // METHODS
  reset: () => {
    set({
      isUpdated: false,
      data: [],
      totalAum: undefined,
      dataView: EmptyDataView,
      dataViews: [],
      columnOptions: [],
      visibleColumns: [],
      filterOptions: [],
      searchTerm: "",
      originalDataView: EmptyDataView,
      viewFilters: [],
      completedConditions: [],
      viewSort: undefined,
      entityType: undefined,
      bookIdOverride: undefined,
      savingCells: {},
      dataQuery: {
        refetch: () => {},
        fetchNextPage: () => {},
        hasNextPage: false,
        isFetchingNextPage: false,
        isError: false,
        error: null,
        isLoading: true,
      },
    })
  },

  onSearch: (searchTerm: string) => {
    set({ searchTerm })
  },

  addViewColumn: (field: string) => {
    const { dataView, checkIfDataViewRemainsChanged, setVisibleColumns } = get()
    set({ isUpdated: true })

    // Find the highest sort order among visible columns
    const visibleViewColumns = dataView.view_columns?.filter((vc) => vc.is_visible) || []
    const maxSortOrder = visibleViewColumns.length > 0 ? Math.max(...visibleViewColumns.map((vc) => vc.sort_order)) : -1
    const newSortOrder = maxSortOrder + 1

    // Get default width from column options or configuration

    const existingColumn = dataView.view_columns?.find((vc) => vc.field === field)

    if (existingColumn) {
      // Update existing column to be visible
      set({
        dataView: {
          ...dataView,
          view_columns: dataView.view_columns?.map((vc) =>
            vc.field === field ? { ...vc, is_visible: true, sort_order: newSortOrder } : vc
          ),
        } as DataView,
      })
    } else {
      // Add new column
      set({
        dataView: {
          ...dataView,
          view_columns: [
            ...(dataView.view_columns || []),
            {
              data_view_id: dataView.id,
              field,
              is_visible: true,
              sort_order: newSortOrder,
            },
          ],
        } as DataView,
      })
    }

    setVisibleColumns()
    checkIfDataViewRemainsChanged()
  },

  removeViewColumn: (field: string) => {
    const { dataView, checkIfDataViewRemainsChanged, setVisibleColumns } = get()
    set({ isUpdated: true })
    set({
      dataView: {
        ...dataView,
        view_columns: dataView.view_columns?.map((c) => (c.field === field ? { ...c, is_visible: false } : c)),
      } as DataView,
    })
    setVisibleColumns()
    checkIfDataViewRemainsChanged()
  },

  swapColumns: (columnA: ViewColumn, columnB: ViewColumn) => {
    const { dataView, checkIfDataViewRemainsChanged, setVisibleColumns } = get()
    set({ isUpdated: true })
    set({
      dataView: {
        ...dataView,
        view_columns: dataView.view_columns?.map((c) => {
          if (c.id === columnA.id) {
            return { ...c, sort_order: columnB.sort_order }
          }
          if (c.id === columnB.id) {
            return { ...c, sort_order: columnA.sort_order }
          }
          return c
        }),
      } as DataView,
    })
    setVisibleColumns()
    checkIfDataViewRemainsChanged()
  },

  updateColumnWidth: (field: string, width: number) => {
    const { dataView, checkIfDataViewRemainsChanged } = get()
    set({ isUpdated: true })
    set({
      dataView: {
        ...dataView,
        view_columns: dataView.view_columns?.map((c) => (c.field === field ? { ...c, column_width: width } : c)),
      } as DataView,
    })
    checkIfDataViewRemainsChanged()
  },

  resetDataView: () => {
    const { originalDataView, setDataView } = get()
    setDataView(originalDataView)
  },

  setConditions: (newConditions: Condition[]) => {
    const { viewFilters, completedConditions, checkIfDataViewRemainsChanged } = get()
    const nextCompleted = newConditions.filter(filterIsComplete)
    if (
      JSON.stringify(viewFilters) === JSON.stringify(newConditions) &&
      JSON.stringify(completedConditions) === JSON.stringify(nextCompleted)
    ) {
      return
    }
    set({ viewFilters: newConditions })
    if (JSON.stringify(completedConditions) !== JSON.stringify(nextCompleted)) {
      set({ completedConditions: nextCompleted })
      set({ isUpdated: true })
      set({ data: [] })
    }
    checkIfDataViewRemainsChanged()
  },

  checkIfDataViewRemainsChanged: () => {
    const { completedConditions, dataView, originalDataView, viewSort } = get()
    const computedDataView = {
      ...dataView,
      condition_group: {
        ...dataView.condition_group,
        conditions: completedConditions,
      },
      view_sorts:
        viewSort && viewSort.field && viewSort.direction
          ? [{ field: viewSort.field, direction: viewSort.direction }]
          : [],
    }
    if (JSON.stringify(computedDataView) === JSON.stringify(originalDataView)) {
      set({ isUpdated: false })
    }
  },

  prepareDataViewForSave: () => {
    const { originalDataView, completedConditions, dataView, viewSort, visibleColumns, columnOptions } = get()
    const known = new Set((columnOptions || []).map((c) => c.accessorKey as string))
    const order = (visibleColumns || []).map((c) => c.accessorKey as string)

    const filtersToRemove = originalDataView.condition_group.conditions.filter(
      (filter) => !completedConditions.map((f) => f.id).includes(filter.id)
    )
    const viewFiltersToSave = [
      ...completedConditions,
      ...filtersToRemove.map((filter) => ({
        ...filter,
        _destroy: true,
      })),
    ]
    const originalSorts = originalDataView.view_sorts || []
    const updatedSortAttrs: Array<{ id?: string; field?: string; direction?: "asc" | "desc"; _destroy?: boolean }> = []
    if (viewSort && viewSort.field && viewSort.direction) {
      const existing = originalSorts[0]
      updatedSortAttrs.push({ id: existing?.id, field: viewSort.field, direction: viewSort.direction })
    }
    originalSorts.slice(viewSort ? 1 : 0).forEach((s) => {
      if (s?.id) updatedSortAttrs.push({ id: s.id, _destroy: true })
    })

    const byField = new Map((dataView.view_columns || []).map((vc) => [vc.field, vc]))
    const normalizedKnownVisible = order
      .map((field, idx) => {
        const vc = byField.get(field)
        if (!vc) return null
        return {
          id: vc.id,
          data_view_id: vc.data_view_id,
          field: vc.field,
          is_visible: vc.is_visible ?? true,
          column_width: vc.column_width,
          sort_order: idx,
        }
      })
      .filter(Boolean)

    const removed = (dataView.view_columns || [])
      .filter((vc) => !order.includes(vc.field) && vc.id)
      .map((vc) => ({ id: vc.id, _destroy: true }))

    const stale = (dataView.view_columns || [])
      .filter((vc) => !known.has(vc.field) && vc.id)
      .map((vc) => ({ id: vc.id, _destroy: true }))

    const viewColumnsToSave = [...normalizedKnownVisible, ...removed, ...stale]

    return {
      ...dataView,
      view_columns: viewColumnsToSave,
      condition_group: {
        ...dataView.condition_group,
        conditions: viewFiltersToSave,
      },
      view_sorts: updatedSortAttrs as ViewSort[],
    } as Partial<DataView> & { id: string }
  },
}))
